"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import CalculatorCrossLinks from "@/components/CalculatorCrossLinks";
import CalculatorReportBridge from "@/components/CalculatorReportBridge";
import { trackCalculatorUsed } from "@/lib/analytics";
import RatingPrompt from "@/components/RatingPrompt";
import { RESULT_REASONS } from "@/lib/feedback/resultReasons";
import { RASHI_DATA, STATUS_STYLES } from "./_data";
import { RashiPicker } from "./_rashi-picker";
import { SadeSatiPhases } from "./_phases";
import { SadeSatiRemedies } from "./_remedies";

/** Saturn transit data for Sade Sati calculation.
 *  Saturn entered Aquarius (Kumbh) Jan 2023 → will be in Pisces (Meen) ~2025-2028.
 *  Affected Moon signs when Saturn is in Aquarius: Makar (12th), Kumbh (1st), Meen (2nd).
 *  As Saturn transitions into Pisces: Kumbh (12th), Meen (1st), Mesh (2nd).
 */

export default function SadeSatiClient({
  defaultLanguage = "en",
}: {
  defaultLanguage?: "en" | "hi";
}) {
  const [selectedRashi, setSelectedRashi] = useState<string | null>(null);
  const hi = defaultLanguage === "hi";

  useEffect(() => {
    trackCalculatorUsed({ calculator: "sade-sati" });
  }, []);

  const affectedRashis = RASHI_DATA.filter((r) => r.currentStatus !== "none");

  return (
    <div className="intent-interface-diagnostic-first sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-12 sm:py-16">
        <SadeSatiHeader />

        {/* Rashi Picker — inline self-check */}
        <RashiPicker selectedRashi={selectedRashi} onSelect={setSelectedRashi} />

        <CurrentSaturnPosition />

        <AffectedRashis affectedRashis={affectedRashis} />

        {/* Soft link for users who don't know their Moon sign */}
        <p className="text-center text-[13px] mb-6" style={{ color: "var(--ish-text-faint)" }}>
          Don&apos;t know your Moon sign?{" "}
          <Link
            href="/rashi-calculator"
            className="font-semibold"
            style={{ color: "var(--ish-accent)" }}
          >
            Find it free →
          </Link>
        </p>

        <AllRashiStatus />

        <SadeSatiPhases />
        <SadeSatiRemedies />

        <CalculatorReportBridge
          planId="sade_sati"
          source={hi ? "/hi/sade-sati" : "/sade-sati"}
          intent="remedy_dosha"
          title={hi ? "Personal Sade Sati Report paayein" : "Get your personal Sade Sati Report"}
          body={
            hi
              ? "Yeh page Moon-sign phase check karta hai. Private report me natal Shani, dasha context, pressure areas aur Shani upay aapki kundali se milte hain."
              : "This page checks Moon-sign phase. The private report reads natal Shani, dasha context, pressure areas, and Shani upay from your own kundali."
          }
          cta={hi ? "Sade Sati Report paayein" : "Get Sade Sati Report"}
          language={defaultLanguage}
        />

        {selectedRashi && (
          <div className="mt-6">
            <RatingPrompt
              source={"tool_result" as never}
              mode="thumbs+reasons"
              reasons={RESULT_REASONS}
              prompt="Was this Sade Sati check accurate?"
              context={{
                surface: "sade_sati_result",
                path: typeof window !== "undefined" ? window.location.pathname : null,
              }}
            />
          </div>
        )}

        <SadeSatiSeoCard />

        {/* Cross Links */}
        <CalculatorCrossLinks currentPage="sade-sati" />
      </div>
    </div>
  );
}

type Rashi = (typeof RASHI_DATA)[number];

function SadeSatiHeader() {
  return (
    <div className="text-center mb-10">
      <p
        className="text-[11px] font-semibold uppercase tracking-[0.25em] mb-3"
        style={{ color: "var(--ish-accent)" }}
      >
        Saturn Transit Calculator
      </p>
      <h1
        className="text-[28px] sm:text-[36px] font-bold leading-tight"
        style={{
          fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
          color: "var(--ish-dark)",
        }}
      >
        Sade Sati: Why These 7.5 Years Feel So Heavy — Check Your Phase &amp; Upay
      </h1>
      <p
        className="text-[18px] mt-1"
        style={{
          fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
          color: "var(--ish-text-muted)",
        }}
      >
        साढ़े साती
      </p>
      <p className="mt-3 text-[14px] max-w-lg mx-auto text-ish-text-muted">
        Saturn&apos;s 7.5-year transit over your Moon sign. Check which rashis are currently
        affected.
      </p>
    </div>
  );
}

function CurrentSaturnPosition() {
  return (
    <div className="card-sacred p-6 sm:p-8 text-center mb-6">
      <span className="block text-[11px] font-semibold uppercase tracking-wider mb-2 text-ish-text-faint">
        Current Saturn Position (2026)
      </span>
      <span
        className="text-[28px] font-bold block"
        style={{
          fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
          color: "var(--ish-dark)",
        }}
      >
        Aquarius (कुंभ)
      </span>
      <span className="text-[13px] block mt-1 text-ish-text-muted">
        ♄ Shani in Kumbh Rashi &middot; Since January 2023
      </span>
    </div>
  );
}

function AffectedRashis({ affectedRashis }: { affectedRashis: Rashi[] }) {
  return (
    <div className="mb-6">
      <h2
        className="text-[18px] font-bold mb-4 text-center"
        style={{
          fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
          color: "var(--ish-dark)",
        }}
      >
        Moon Signs Currently Under Sade Sati
      </h2>
      <div className="space-y-3">
        {affectedRashis.map((rashi) => (
          <AffectedRashiCard key={rashi.id} rashi={rashi} />
        ))}
      </div>
    </div>
  );
}

function AffectedRashiCard({ rashi }: { rashi: Rashi }) {
  const styles = STATUS_STYLES[rashi.currentStatus];
  return (
    <div
      className="card-sacred p-5"
      style={{ borderColor: styles.border, backgroundColor: styles.bg }}
    >
      <div className="flex items-start justify-between">
        <div>
          <span
            className="text-[18px] font-bold"
            style={{
              fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
              color: styles.text,
            }}
          >
            {rashi.nameEn}
          </span>
          <span className="text-[14px] ml-2" style={{ color: styles.text }}>
            {rashi.nameHi}
          </span>
        </div>
        <span
          className="text-[11px] font-semibold px-2.5 py-1 rounded shrink-0"
          style={{ backgroundColor: styles.badgeBg, color: styles.badge }}
        >
          {phaseLabel(rashi.currentStatus, true)}
        </span>
      </div>
      <p className="text-[13px] mt-2" style={{ color: styles.text }}>
        {phaseDescription(rashi.currentStatus)}
      </p>
      <p className="text-[12px] mt-2 text-ish-text-faint">
        Approximate period: {rashi.approximatePeriod}
      </p>
    </div>
  );
}

function phaseLabel(status: Rashi["currentStatus"], uppercase = false) {
  const labels = { rising: "Rising", peak: "Peak", setting: "Setting", none: "Not Active" };
  const label = status === "none" ? labels.none : `${labels[status]} Phase`;
  return uppercase ? label.toUpperCase() : label.replace(" Phase", "");
}

function phaseDescription(status: Rashi["currentStatus"]) {
  const descriptions = {
    rising:
      "Rising phase asks for tighter budgets, steadier sleep routines, and quieter inner discipline. Treat unease as a signal to reduce leakage and prepare.",
    peak: "Peak phase puts identity, confidence, health habits, and public responsibility under pressure. The work is to simplify, endure, and mature.",
    setting:
      "Setting phase turns attention to family commitments, money reserves, speech, and food discipline. Lessons begin to settle into practical wisdom.",
    none: "",
  };
  return descriptions[status];
}

function AllRashiStatus() {
  return (
    <div className="card-sacred p-6 sm:p-8">
      <h2
        className="text-[18px] font-bold mb-4"
        style={{
          fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
          color: "var(--ish-dark)",
        }}
      >
        Sade Sati Status for All 12 Rashis
      </h2>
      <div className="space-y-1.5">
        {RASHI_DATA.map((rashi) => (
          <RashiStatusRow key={rashi.id} rashi={rashi} />
        ))}
      </div>
    </div>
  );
}

function RashiStatusRow({ rashi }: { rashi: Rashi }) {
  const styles = STATUS_STYLES[rashi.currentStatus];
  const isActive = rashi.currentStatus !== "none";
  return (
    <div
      className="flex items-center justify-between p-3 rounded-lg"
      style={{ backgroundColor: styles.bg, border: `1px solid ${styles.border}` }}
    >
      <div className="flex items-center gap-2">
        <span
          className="text-[14px] font-semibold"
          style={{ color: isActive ? styles.text : "var(--ish-dark)" }}
        >
          {rashi.nameEn}
        </span>
        <span className="text-[12px] text-ish-text-faint">{rashi.nameHi}</span>
      </div>
      <div className="text-right">
        {isActive ? (
          <span
            className="text-[12px] font-semibold px-2 py-0.5 rounded"
            style={{ backgroundColor: styles.badgeBg, color: styles.badge }}
          >
            {phaseLabel(rashi.currentStatus)}
          </span>
        ) : (
          <span className="text-[12px] text-ish-text-faint">
            {rashi.currentPhaseLabel === "Recently Ended" ? "Recently Ended" : "Not Active"}
          </span>
        )}
      </div>
    </div>
  );
}

function SadeSatiSeoCard() {
  return (
    <div className="mt-12 card-sacred p-6 sm:p-8">
      <h2
        className="text-[20px] font-bold mb-4"
        style={{
          fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
          color: "var(--ish-dark)",
        }}
      >
        About Sade Sati
      </h2>
      <div className="space-y-3 text-[14px] leading-relaxed text-ish-text-muted">
        <p>
          <strong style={{ color: "var(--ish-dark)" }}>Sade Sati</strong> (साढ़े साती) is the
          7.5-year transit of Saturn over three houses centered on your Moon sign. It is considered
          one of the most significant periods in Vedic astrology, testing discipline, patience, and
          karmic balance. The name means &quot;seven and a half&quot; in Hindi.
        </p>
        <p>
          To determine if you are currently under Sade Sati, you need to know your{" "}
          <strong style={{ color: "var(--ish-dark)" }}>Moon sign (Janma Rashi)</strong> — not your
          Sun sign. Use our{" "}
          <Link
            href="/rashi-calculator"
            className="font-semibold"
            style={{ color: "var(--ish-accent)" }}
          >
            Rashi Calculator
          </Link>{" "}
          to find your Moon sign instantly by entering your birth details.
        </p>
        <p>
          For a complete analysis of Saturn&apos;s influence on your birth chart, including its
          house placement, aspects, and Dasha periods, generate your{" "}
          <Link
            href="/free-kundali/"
            className="font-semibold"
            style={{ color: "var(--ish-accent)" }}
          >
            Free Kundali
          </Link>
          . Also check today&apos;s{" "}
          <Link href="/rahu-kaal/" className="font-semibold" style={{ color: "var(--ish-accent)" }}>
            Rahu Kaal
          </Link>{" "}
          for daily inauspicious timings.
        </p>
      </div>
    </div>
  );
}
